import { useState, useEffect } from 'react';
import { useCashUniverse } from '~Components/CashUniverse';
import { useReportsUniverse } from '~Components/ReportsUniverse';
import { useStorage } from '~Components/Storage';
import { useMetadata, useSelector } from '~Hooks';

/**
 * This hook is to ensure we only display the start of day component <DayNotStarted /> when it should
 * There are some settings that can make this different, and to ensure the same behaviour across the app, we use this hook.
 */
export const useStatusOfDay = () => {
  const [storage] = useStorage();
  const report = useReportsUniverse();
  const metadata = useMetadata();

  const flags = useSelector((state) => state.companyFlags);
  const reportSnapshot = useSelector((state) => state.reportSnapshot);
  const paymentMethods = useSelector((state) => state.POSPaymentMethods);

  const hasCashAsPaymentMethod = paymentMethods.some((method) => method.id === 'CASH');

  const cashUniverse = useCashUniverse(); // This is device-specific

  // State to track if the day has started
  const [hasDayStarted, setHasDayStarted] = useState<boolean>(false);

  useEffect(() => {
    /** If they don't have cash there is no reason to prompt a start of day */
    if (!hasCashAsPaymentMethod || storage.disableCash) {
      setHasDayStarted(true);
      return;
    }

    /**
     * enableAutoCashEOD closes all devices with their respective totalCashRegisterAmount by an SNS event in Galaxy at the end of the day.
     * This setting should only be used with hasIndividualEOD - therefore we check:
     * If the cash register hasn't had a start of the day, it doesn't matter if report.dayStarted is true - we need to open this specific device with the initial cash amount.
     */
    if (metadata.data.enableAutoCashEOD || metadata.data.hasIndividualEOD) {
      setHasDayStarted(cashUniverse.dayStarted ?? false);
      return;
    }

    /**
     * Here, the company has been configured to handle all openings & endings of the day manually.
     * Therefore, we need to check if the report has been started at all on any device - if not, start it.
     */
    if (flags.manualEndOfDay) {
      setHasDayStarted(!reportSnapshot?.loading && Boolean(report.dayStarted));
      return;
    }

    /**
     * This is if the company has not configured to start the day manually - then we don't render <DayNotStarted /> component.
     */
    setHasDayStarted(true);
  }, [
    metadata.data.enableAutoCashEOD,
    metadata.data.hasIndividualEOD,
    cashUniverse.dayStarted,
    flags.manualEndOfDay,
    reportSnapshot?.loading,
    report.dayStarted,
    hasCashAsPaymentMethod,
  ]);

  return { hasDayStarted };
};
